import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useAppContextSelector } from 'src/app-components/AppContext'

import { LeadsQuery, OpportunitiesQuery } from '@app/api/EntityQuery'
import { FilterQueryParams } from '@app/api/FilterQueryParams'
import { ContactResponse } from '@app/types'
import { routes } from '@app/utils/routes'

import { renderScheduledItem } from './renderListItem'
import { SalesActivityList } from './SalesActivityList'

// -----------------------------
// * HELPERS / CONSTANTS
// -----------------------------

const today = new Date()
const weekFromToday = new Date(today)
today.setUTCHours(0, 0, 0, 0)  // Set to start of day in UTC

weekFromToday.setDate(today.getDate() + 7)

function getScheduledLeadsQuery(userContact: ContactResponse) {
  const query = new LeadsQuery()
  const fq = new FilterQueryParams()

  const isScheduledForThisWeek = fq.and(
    fq.dateFilter({ field: 'nextFollowUp', operation: 'greaterThanOrEqual', value: today }),
    fq.dateFilter({ field: 'nextFollowUp', operation: 'lessThan', value: weekFromToday }),
  )
  const isAssignedToMe = fq.textFilter({ field: 'primaryRep.id', operation: 'equals', value: userContact.id })

  // Appropriate for all sales activity lists
  const isNotQualified = fq.textFilter({ field: 'status.lookup', operation: 'notEqual', value: 'QUALIFIED' })
  const isNotInterested = fq.textFilter({ field: 'status.lookup', operation: 'notEqual', value: 'NOT_QUALIFIED' })
  // ----------------------------

  query.filter(fq.and(isAssignedToMe, isScheduledForThisWeek, isNotQualified, isNotInterested))

  return query
}

function getScheduledOppsQuery(userContact: ContactResponse) {
  const query = new OpportunitiesQuery()
  const fq = new FilterQueryParams()

  const isScheduledForThisWeek = fq.and(
    fq.dateFilter({ field: 'nextFollowUp', operation: 'greaterThanOrEqual', value: today }),
    fq.dateFilter({ field: 'nextFollowUp', operation: 'lessThan', value: weekFromToday }),
  )

  const isAssignedToMe = fq.group(
    fq.or(
      fq.textFilter({ field: 'accountPortfolioManager.id', operation: 'equals', value: userContact.id }),
      fq.textFilter({ field: 'businessDevelopmentManager.id', operation: 'equals', value: userContact.id }),
      fq.textFilter({ field: 'insideSalesRep.id', operation: 'equals', value: userContact.id }),
      fq.setFilter({ field: 'estimators.id', values: [userContact.id] }),
    ),
  )

  // Appropriate for all sales activity lists
  const isNotWon = fq.textFilter({ field: 'stage', operation: 'notEqual', value: 'CLOSED_WON' })
  const isNotLost = fq.textFilter({ field: 'stage', operation: 'notEqual', value: 'CLOSED_LOST' })
  // ----------------------------

  query.filter(fq.and(isAssignedToMe, isScheduledForThisWeek, isNotWon, isNotLost))

  return query
}

function getScheduledFilterModel() {
  const gteToday = {
    dateFrom: today,
    dateTo: null as null,
    filterType: 'date',
    type: 'greaterThan',
  }

  const lteOneWeekFromToday = {
    dateFrom: weekFromToday,
    dateTo: null as null,
    filterType: 'date',
    type: 'lessThan',
  }

  // AGG Filter Model format
  const scheduledFilterModel = {
    filterType: 'date',
    operator: 'AND',
    conditions: [gteToday, lteOneWeekFromToday],
  }

  return scheduledFilterModel
}

const SCHEDULED_OPPS_URL = (function () {
  const searchParams = new URLSearchParams()

  searchParams.set('view', 'my')
  searchParams.set('filterModel', btoa(JSON.stringify({ nextFollowUp: getScheduledFilterModel() })))

  return `${routes.opportunities}?${searchParams.toString()}`
})()

const SCHEDULED_LEADS_URL = (function () {
  const searchParams = new URLSearchParams()

  searchParams.set('view', 'my')
  searchParams.set('filterModel', btoa(JSON.stringify({ nextFollowUp: getScheduledFilterModel() })))

  return `${routes.leads}?${searchParams.toString()}`
})()

// -----------------------------
// * COMPONENT
// -----------------------------

export function ScheduledList() {
  const userContact = useAppContextSelector((state) => state.userContact)

  const leadsQuery = getScheduledLeadsQuery(userContact)
  const oppsQuery = getScheduledOppsQuery(userContact)

  const moreActions = (
    <div className="flex w-full flex-col gap-4xs py-xs text-[14px]">
      <Link className="px-[8px] py-[6px]" href={SCHEDULED_LEADS_URL}>
        View Scheduled Leads
      </Link>
      <Link className="px-[8px] py-[6px]" href={SCHEDULED_OPPS_URL}>
        View Scheduled Opportunities
      </Link>
    </div>
  )

  return (
    <SalesActivityList
      header="Scheduled · This Week"
      moreActions={moreActions}
      emptyMessage="Nothing Scheduled"
      emptyIcon={<Image alt="calendar icon" width={32} height={32} src="/calendar.png" />}
      renderListItem={renderScheduledItem}
      leadsQuery={leadsQuery}
      oppsQuery={oppsQuery}
    />
  )
}
